class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.drawer = document.querySelector('.wt-drawer-search');
    this.body = document.body;
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector('.wt-header__search__clear-button');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.cachedResults = {};
    this.originalMiddleColumnContent = '';
    this.classDrawerActive = 'wt-drawer-search--active';
    this.activeOverlayBodyClass = 'search-overlay-on';
    
    this.init();
  }

  storeOriginalMiddleColumnContent() {
    const middleColumn = document.querySelector('.search-results-redesign__products');
    if (middleColumn) {
      this.originalMiddleColumnContent = middleColumn.innerHTML;
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  // ===========================================
  // INITIALIZATION
  // ===========================================
  init() {


    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => {
        this.input.value = "";
        this.clearButton.style.display = "none";
        this.isVisibleClearButton = false;
        this.clearResults();
      });
    }

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    if (this.input) {
      this.input.addEventListener("input", () => {
        if (this.input.value.length > 0 && !this.isVisibleClearButton) {
          this.clearButton.style.display = "flex";
          this.clearButton.setAttribute("tabindex", "0");
          this.isVisibleClearButton = true;
        } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
          this.clearButton.style.display = "none";
          this.clearButton.setAttribute("tabindex", "-1");
          this.isVisibleClearButton = false;
        }
      });
    }
  }

  // ===========================================
  // EVENT LISTENERS SETUP
  // ===========================================
  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }
  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
      // Show search results in middle column immediately when typing
      this.showSearchResultsInMiddleColumn();
    } else {
      this.clearResults();
    }
  }

  // ===========================================
  // SEARCH RESULTS DISPLAY
  // ===========================================
  showSearchResultsInMiddleColumn() {
    const middleColumn = document.querySelector('.search-results-redesign__products');
    if (middleColumn) {
      this.showLoadingState(middleColumn);
    }
  }

  showLoadingState(middleColumn) {
    fetchLoadingState(middleColumn);
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();
    
    if (searchTerm.length > 0) {
      event.preventDefault();
      window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
      return;
    }
    if (
      !searchTerm.length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  // ===========================================
  // SEARCH RESULTS FETCHING
  // ===========================================
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  // ===========================================
  // ACCESSIBILITY - LIVE REGION STATES
  // ===========================================
  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  // ===========================================
  // SEARCH RESULTS RENDERING
  // ===========================================
  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.updateMiddleColumnWithPredictiveResults(resultsMarkup);

    this.setAttribute("results", true);

    this.setLiveRegionResults();
  }

  updateMiddleColumnWithPredictiveResults(resultsMarkup) {
    const middleColumn = document.querySelector('.search-results-redesign__products');
    if (middleColumn) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');
      const productList = doc.querySelector('#predictive-search-results-list');
      
      if (productList && productList.children.length > 0) {
        const searchResultsHTML = `
          <div class="search-results-redesign__products-grid">
            ${productList.outerHTML}
          </div>
        `;
        middleColumn.innerHTML = searchResultsHTML;
      } else {
        this.showNoResults(middleColumn);
      }
    }
  }

  // ===========================================
  // NO RESULTS HANDLING
  // ===========================================
  showNoResults(middleColumn) {
    this.fetchNoResultsState(middleColumn);
  }

  fetchNoResultsState(middleColumn) {
    const noResultsUrl = `${window.routes.search_url}?section_id=search-results-ajax&state=no-results`;
    
    fetch(noResultsUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const noResultsState = doc.querySelector('#ajax-search-results');
        
        if (noResultsState) {
          middleColumn.innerHTML = noResultsState.innerHTML;
        } else {
          middleColumn.innerHTML = '<h3 class="search-results-redesign__title">No Results Found</h3>';
        }
      })
      .catch(error => {
        middleColumn.innerHTML = '<h3 class="search-results-redesign__title">No Results Found</h3>';
      });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  // ===========================================
  // RESULTS CLEARING
  // ===========================================
  clearResults() {
    this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    
    this.restoreOriginalMiddleColumn();
  }

  restoreOriginalMiddleColumn() {
    const middleColumn = document.querySelector('.search-results-redesign__products');
    if (middleColumn && this.originalMiddleColumnContent) {
      middleColumn.innerHTML = this.originalMiddleColumnContent;
    }
    
    const leftColumn = document.querySelector('.search-results-redesign__suggestions');
    if (leftColumn) {
      const title = leftColumn.querySelector('.search-results-redesign__title');
      if (title) {
        title.style.display = '';
      }
    }
  }
}

customElements.define("search-drawer", DrawerSearchSection);

// Ensure all close buttons are hidden on page load (drawer visibility handled by page-header)
(function() {
  const closeButtons = document.querySelectorAll('.wt-header__search__close');
  closeButtons.forEach(closeButton => {
    if (closeButton) {
      closeButton.style.display = 'none';
    }
  });
})();
